<script lang="ts">
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import EntityLayoutActionsMenu from '#entities/components/layouts/entity_layout_actions_menu.svelte'
  import type { SerializedEntity } from '#entities/lib/entities'
  import { entityTypeNameBySingularType } from '#entities/lib/types/entities_types'
  import { i18n, I18n } from '#user/lib/i18n'
  import EmbeddedWelcome from '#welcome/components/embedded_welcome.svelte'
  import CopiableText from './copiable_text.svelte'
  import type { Snippet } from 'svelte'

  let {
    entity,
    flash = $bindable(),
    showEntityEditButtons = true,
    typeLabel = entity.type,
    entityLayoutMain,
  }: {
    entity: SerializedEntity
    flash?: FlashState
    showEntityEditButtons?: boolean
    typeLabel?: string
    entityLayoutMain: Snippet
  } = $props()

  const uri = $derived(entity.uri)
  const wdUri = $derived(entity.wdUri)
  const invUri = $derived(entity.invUri)
  const labelKey = $derived(entityTypeNameBySingularType[typeLabel] || 'subject')
  // Use an intermediary step, as testing in the template part raises a "types have no overlap" error(?!)é
  const showInvUri = $derived(invUri && invUri !== uri)
</script>

<div class="layout">
  <div class="header-wrapper">
    <div class="header">
      <h2 class="type-label">{I18n(labelKey)}</h2>
    </div>
    <EntityLayoutActionsMenu
      {entity}
      {showEntityEditButtons}
    />
  </div>
  <div class="entity-wrapper">
    {@render entityLayoutMain()}
  </div>
  <Flash state={flash} />
  <div class="entity-data-wrapper">
    <p class="uri">
      {I18n(typeLabel)}
      - <CopiableText text={uri} buttonTitle={i18n('Copy URI to clipboard')} classes="uri classic-link" />
      {#if wdUri && wdUri !== uri}
        - <CopiableText text={wdUri} buttonTitle={i18n('Copy URI to clipboard')} classes="uri classic-link" />
      {/if}
      {#if showInvUri}
        - <CopiableText text={invUri} buttonTitle={i18n('Copy URI to clipboard')} classes="uri classic-link" />
      {/if}
    </p>
  </div>
</div>

<EmbeddedWelcome />

<style lang="scss">
  @import "general/scss/utils";
  .layout{
    @include display-flex(column, stretch, center);
    margin: 0 auto;
    max-inline-size: 84em;
    background-color: white;
  }
  .type-label{
    color: $grey;
    font-size: 1.1rem;
    @include sans-serif;
  }
  .header-wrapper{
    display: flex;
    margin: 1em 0;
  }
  .header{
    @include display-flex(row, center, center);
    inline-size: 100%;
  }
  .entity-wrapper{
    @include display-flex(column, flex-start);
  }
  .entity-data-wrapper{
    @include display-flex(column, center);
  }
  /* Large screens */
  @media screen and (width >= $small-screen){
    .layout{
      padding: 0 1em;
    }
    .header-wrapper{
      margin-inline-start: 1.2em;
    }
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .layout{
      padding: 0 0.5em;
    }
    .header-wrapper{
      @include display-flex(row, center, space-between);
    }
    .entity-wrapper{
      @include display-flex(column, flex-start);
    }
  }
  /* Very Small screens */
  @media screen and (width < $very-small-screen){
    .layout{
      padding: 0 0.5em;
    }
  }
</style>
